import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material"
import { FC, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useAppDispatch } from "src/app/hooks"
import { thingsActions } from "src/features/things/thingsSlice"
import {
  hasUsableLocation,
  isCandidateCountrySupported,
} from "src/hooks/convertArcgisCandidateToMblsAddress"
import useArcgisFindAddressCandidates from "src/hooks/useArcgisFindAddressCandidates"
import useArcgisSuggestions from "src/hooks/useArcgisSuggestions"
import { createClientObject } from "src/mblsDelivery/createClientObject"
import { postMessageRequest } from "src/postMessageRequest"
import FormFields from "./FormFields"
import {
  buildCreateMblsClientFields,
  createMblsClientJoiSchema,
} from "./osCreateMblsClientFormFields"
import { Sl2Btn } from "./Sl2Btn"
import { Sl2FormContainer } from "./Sl2FormContainer"

type OsCreateMblsClientDialogProps = {
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

export const OsCreateMblsClientDialog: FC<OsCreateMblsClientDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation()

  const [error, setError] = useState(false)

  const arc = useArcgisSuggestions()
  const arcCandi = useArcgisFindAddressCandidates()

  const [localAddress, setLocalAddress] = useState<string>()

  const onAddressChange = useCallback(
    (e: any) => {
      setLocalAddress(e?.target?.value)
      arc.refresh({
        text: e?.target?.value || "",
        maxSuggestions: 5,
      })
    },
    [arc],
  )

  const [defaultValues, setDefaultValues] = useState({
    phoneLabel: "Mobile",
    addressLabel: "Home",
    addressArcgis: "",
  })

  /**
   * LOCKER-410 `DoD3`: a geocode is issued only for a suggestion the user
   * actually picked. Typing used to call `findCandidates` with an empty
   * `magicKey` on every keystroke, which asked ArcGIS to guess — the route by
   * which an out-of-country candidate reached a converter that could not
   * represent it.
   */
  const onAddressInputChange = useCallback(
    (value: string, magicKey: string) => {
      setLocalAddress(value)
      if (magicKey) {
        setDefaultValues((prev) => ({ ...prev, addressArcgis: value }))
      }
      // Delegated unconditionally on purpose: the hook refuses to post without
      // a magic key AND clears whatever candidate was resolved last. Returning
      // early here instead would leave a rejected address's error pinned to the
      // field while the operator types a new one.
      arcCandi.findCandidates(value, magicKey)
    },
    [arcCandi],
  )

  /**
   * The address this dialog is allowed to submit, or `undefined`.
   *
   * **This, not a boolean flag, is what gates submit** (round 1, F1.1). The
   * first cut keyed the gate on `addressError`, which is derived from
   * `arcCandi.candidates[0]` — and typing a single character clears that,
   * because `findCandidates` refuses an empty magic key and resets its state.
   * The red message vanished, Créer re-enabled, and the form still held the
   * rejected address string, so a `createClient` went out with
   * `mblsAddress: undefined`. `createClientObject` cannot stop that: its own
   * input validation is commented out. Gating on the payload closes the
   * one-keystroke path and the never-selected path together.
   */
  const selectedAddress = useMemo(() => {
    // A geocode in flight means the candidate still in state belongs to the
    // PREVIOUS address (round 2, F2.3): `findCandidates` only clears on the
    // `!magicKey` branch, so picking a second suggestion leaves the first one
    // resolved until the answer lands. Submitting inside that window posted the
    // first address under the second one's label. Treating "in flight" as "no
    // address" closes it — the same shape as F1.1, a gate reading state that no
    // longer matches what the operator sees.
    if (arcCandi.isLoading) return undefined
    const candidate = arcCandi.candidates[0]
    const address = arcCandi.mblsAddresses[0]
    if (!candidate || !address) return undefined
    return isCandidateCountrySupported(candidate) &&
      hasUsableLocation(candidate)
      ? address
      : undefined
  }, [arcCandi.candidates, arcCandi.isLoading, arcCandi.mblsAddresses])

  /**
   * LOCKER-410 `DoD11` / `N1`: a candidate whose `Country` is missing or
   * outside `{CAN, USA}` is rejected here, visibly, rather than accepted with
   * an empty country component. The legacy `Client` save path consumes that
   * component, so "accept empty" only moves the failure somewhere the operator
   * cannot see it.
   *
   * The third branch exists so the disabled Créer is never mute: once the text
   * no longer corresponds to a resolved address, the operator is told to pick
   * one again instead of clicking a dead button.
   *
   * It is keyed on `defaultValues.addressArcgis` — set only when a suggestion
   * was actually picked — and suppressed while a lookup is in flight. Keying it
   * on `localAddress` made the field red from the FIRST character typed and
   * kept it red across the geocode roundtrip (round 2, F2.2): erroring a field
   * the operator is still filling is how people are trained to ignore the very
   * channel `DoD11` needs them to read.
   */
  const addressError = useMemo(() => {
    if (arcCandi.isLoading) return undefined
    const candidate = arcCandi.candidates[0]
    if (candidate) {
      if (!isCandidateCountrySupported(candidate)) {
        return t("addressCountryNotSupported")
      }
      if (!hasUsableLocation(candidate)) return t("addressCouldNotBeResolved")
      return undefined
    }
    return defaultValues.addressArcgis && !selectedAddress
      ? t("addressMustBeSelected")
      : undefined
  }, [
    arcCandi.candidates,
    arcCandi.isLoading,
    defaultValues.addressArcgis,
    selectedAddress,
    t,
  ])

  const { fields0, fields1 } = useMemo(
    () =>
      buildCreateMblsClientFields({
        t,
        isSuggestionsLoading: arc.isLoading,
        suggestions: arc.suggestions,
        localAddress,
        onAddressChange,
        onAddressInputChange,
        addressError,
      }),
    [
      addressError,
      arc.isLoading,
      arc.suggestions,
      localAddress,
      onAddressChange,
      onAddressInputChange,
      t,
    ],
  )

  const [isLoading, setIsLoading] = useState(false)

  const dispatch = useAppDispatch()

  return (
    <Dialog data-ui="UI-SL2W-052"
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="md"
      // `fullWidth` pins the dialog at `maxWidth` instead of letting it size to
      // its content: without it the box visibly resized whenever a validation
      // message appeared under the address field.
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, boxShadow: "0 12px 40px rgba(15, 23, 42, 0.18)" },
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ px: 3, pt: 2.5, pb: 2 }}
      >
        <DialogTitle
          id="alert-dialog-title"
          // The Stack already owns the header's padding; DialogTitle's own
          // 16/24 padding on top of it is what made the title sit low and
          // pushed the progress spinner off the text's baseline.
          sx={{ p: 0, fontSize: "1.125rem", fontWeight: 600, lineHeight: 1.4 }}
        >
          {t("createClient")}
        </DialogTitle>
        {isLoading && <CircularProgress size={18} />}
      </Stack>
      <Divider />
      <DialogContent
        sx={{
          px: 3,
          pt: 3,
          pb: 2,
          // Scoped to this dialog on purpose — `FormFields` is shared by every
          // form in the console, so softening the inputs there would restyle
          // screens this ticket never looked at.
          "& .MuiOutlinedInput-root": {
            borderRadius: 1.5,
            bgcolor: "background.paper",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "grey.300" },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "grey.400",
            },
          },
          "& .MuiInputLabel-root": { fontWeight: 500 },
        }}
      >
        <Stack direction="column" spacing={1}>
          <Sl2FormContainer
            joiSchema={createMblsClientJoiSchema}
            onSuccess={async (values) => {
              setError(false)
              // Gate on the PAYLOAD, not on the error flag — the flag is
              // derived from candidate state the operator can clear with one
              // keystroke (round 1, F1.1). The button is already disabled in
              // this state; this is the half that holds for a keyboard submit.
              if (!selectedAddress) return
              // for phoneNumber, remove () and - and space
              const phoneNumber = values.phoneNumber.replace(/[\s()-]/g, "")
              const mblsClient = createClientObject({
                ...values,
                phoneNumber,
                mblsAddress: selectedAddress,
              } as any)
              setIsLoading(true)
              try {
                const res = await postMessageRequest(window.parent, {
                  type: "createClient",
                  payload: mblsClient,
                })
                setDefaultValues({
                  phoneLabel: "Mobile",
                  addressLabel: "Home",
                  addressArcgis: "",
                })
                if (res?.payload?.client?.id) {
                  dispatch(
                    thingsActions.updateClientSelectionDefaultValues({
                      clientId: res?.payload?.client?.id,
                    }),
                  )
                }
                dispatch(
                  thingsActions.setClientSelectionSearchValues({
                    firstName: values.firstName,
                    lastName: values.lastName,
                  }),
                )

                // wailt 500 ms to let redux dispatch finish
                setTimeout(() => {
                  onSuccess()
                }, 500)
              } catch (e: any) {
                console.error(e)
                setError(true)
              } finally {
                setIsLoading(false)
                setLocalAddress("")
              }
            }}
          >
            <FormFields fields={fields0} defaultValues={defaultValues} />
            <Box pt={2} />
            <FormFields fields={fields1} defaultValues={defaultValues} />
            <DialogActions sx={{ px: 0, pt: 3, pb: 0, gap: 1 }}>
              <Sl2Btn
                onClick={onClose}
                color="inherit"
                disabled={isLoading}
                // Two equally-weighted primary-coloured buttons side by side
                // give the operator nothing to aim at. Cancel recedes; Créer
                // stays the one that looks clickable.
                sx={{ minWidth: 104, color: "text.secondary" }}
              >
                {t("cancel")}
              </Sl2Btn>
              <Sl2Btn
                type="submit"
                color="primary"
                variant="contained"
                disableElevation
                disabled={isLoading || !selectedAddress}
                // Fixed width so the button does not shrink to the spinner's
                // size the moment it is pressed.
                sx={{ minWidth: 104, fontWeight: 600 }}
              >
                {isLoading ? <CircularProgress size={16} /> : t("create")}
              </Sl2Btn>
            </DialogActions>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="flex-end"
            >
              {error && (
                <Typography variant="caption" color="error">
                  {t("unableToCreateClient")}
                </Typography>
              )}
            </Stack>
          </Sl2FormContainer>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}
